import { client, urlFor, getSanityEditLink } from "./sanity-client";
import initScrollAnimations from "./animations";

// ── DOM refs (templated by case-study.html) ───────────────────
const root = document.querySelector('.cs-detail');
const heroContainer = document.getElementById('case-study-hero');
const contentContainer = document.getElementById('case-study-content');
const tagsContainer = document.getElementById('cs-tags');
const nextCardContainer = document.getElementById('cs-next-card');
const talkCtaContainer = document.getElementById('cs-talk-cta');
const tocList = document.getElementById('tocList');
const progressBar = document.getElementById('progressBar');

// ── Tag swatch palette (matches the reference design) ─────────
const TAG_SWATCH = {
  'mobile': '#2A6FDB',
  'platform': '#7A55C2',
  'cloud': '#2A6FDB',
  'security': '#C2691F',
  'storage': '#1F8A5B',
  'ai': '#7A55C2',
  'ai & ml': '#7A55C2',
  'ai/ml': '#7A55C2',
  'ml': '#7A55C2',
  'devops': '#1F8A5B',
  'qa': '#C2691F',
  'react native': '#C2691F',
  'c++': '#1F8A5B',
};
const swatchFor = (tag) => TAG_SWATCH[String(tag || '').toLowerCase().trim()] || '#5A6470';

// ── Helpers ───────────────────────────────────────────────────
const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

const isConclusion = (section) =>
  /^conclusion$/i.test(String(section?.title || '').trim());

function getStudyTagList(study) {
  if (Array.isArray(study?.tags) && study.tags.length) {
    return study.tags.filter(Boolean);
  }
  if (study?.tag) {
    return String(study.tag).split(',').map((t) => t.trim()).filter(Boolean);
  }
  return [];
}

// ── Sanity fetch ──────────────────────────────────────────────
// Lookup is keyed by EITHER slug or title. Slug is the new canonical
// (set via ?slug=… in the URL); title is the legacy fallback used by
// pre-slug shared links and by the per-study HTML files that still
// pass `data-title`. The htaccess at src/.htaccess 301-redirects the
// known production ?title= URLs to ?slug= so this fallback is mostly
// belt-and-braces for development / unforeseen paths.
async function fetchCaseStudy({ slug, title }) {
  try {
    const query = `*[_type == "caseStudy" && !(_id in path("drafts.**")) && visibility != false] | order(_createdAt desc) {
        _id,
        title,
        description,
        tag,
        tags,
        image,
        heroImage,
        showHero,
        showSections,
        showTags,
        showRelated,
        showCta,
        showHeroEyebrow,
        heroEyebrow,
        heroDescription,
        "slug": slug.current,
        sections[]{..., _key},
        "relatedCaseStudies": relatedCaseStudies[@->visibility != false]->{
            _id, title, description, tag, tags, image,
            "slug": slug.current
        }
    }`;
    const caseStudies = await client.fetch(query);
    if (!caseStudies || caseStudies.length === 0) {
      return { currentStudy: null, allCaseStudies: [], docId: null };
    }
    let currentStudy = null;
    if (slug) {
      currentStudy = caseStudies.find((cs) => cs.slug === slug);
    }
    if (!currentStudy && title) {
      const lower = title.toLowerCase();
      currentStudy =
        caseStudies.find((cs) => cs.title && cs.title.toLowerCase() === lower) ||
        caseStudies.find((cs) => cs.title && cs.title.toLowerCase().includes(lower));
    }
    return {
      currentStudy,
      allCaseStudies: caseStudies,
      docId: currentStudy ? currentStudy._id : null,
    };
  } catch (error) {
    console.error('Error fetching case study:', error);
    return null;
  }
}

// ── Renderers ─────────────────────────────────────────────────
// Resolve the Sanity hero image to a CDN URL, or null if the study has
// no image set. Preference order: dedicated heroImage → cover image →
// none. This lets authors set a hero-specific image without losing
// support for legacy case studies that only carry the cover image.
// Width is generous (1920) so the image stays crisp on retina
// widescreens where it spans the full hero.
function resolveHeroImageUrl(study) {
  // Images are intentionally ignored for now — every case study renders
  // the no-image hero treatment regardless of whether Sanity has a
  // heroImage / cover image set. Re-enable by restoring the original
  // resolution logic below.
  return null;
  // eslint-disable-next-line no-unreachable
  if (!study) return null;
  const source = (study.heroImage && study.heroImage.asset)
    ? study.heroImage
    : (study.image && study.image.asset ? study.image : null);
  if (!source) return null;
  try {
    return urlFor(source).width(1920).quality(80).url();
  } catch (err) {
    console.warn('Could not resolve case study hero image:', err);
    return null;
  }
}

// Resolve the COVER image (always study.image, never heroImage) at a
// lower resolution — it's used purely as a heavy-blurred backdrop
// behind the hero ("atmosphere" effect), so it doesn't need to be
// pixel-sharp and a smaller download saves bytes.
function resolveCoverImageUrl(study) {
  // Atmosphere backdrop is suppressed alongside the hero image — the
  // page is currently rendered as if no images were authored.
  return null;
  // eslint-disable-next-line no-unreachable
  if (study && study.image && study.image.asset) {
    try {
      return urlFor(study.image).width(1200).quality(55).url();
    } catch (err) {
      console.warn('Could not resolve case study cover image:', err);
    }
  }
  return null;
}

function renderHero(study, docId) {
  if (!heroContainer) return;
  const tags = getStudyTagList(study);
  const showMeta = study.showTags !== false && tags.length > 0;
  const dek = study.heroDescription || study.description || '';
  // Eyebrow is rendered when the text is non-empty AND the visibility
  // toggle (Sanity field `showHeroEyebrow`) is not explicitly false.
  // Existing studies without the field default to showing it.
  const eyebrow = study.showHeroEyebrow === false
    ? ''
    : (study.heroEyebrow || '').trim();
  const heroImg = resolveHeroImageUrl(study);
  const atmosphereImg = resolveCoverImageUrl(study);

  // .is-fullbleed is the trigger for the dark scrim + light text colors
  // (and, at ≥1300px, the right-bleed two-column layout). Only applied
  // when we actually have an image. Without one, .is-no-image lets the
  // text span the full hero width on every viewport — no empty image
  // column, no 900px centering cap.
  heroContainer.classList.toggle('is-fullbleed', !!heroImg);
  heroContainer.classList.toggle('is-no-image', !heroImg);

  // Two image elements share the same Sanity URL:
  //   .hero-img — full-bleed background at <1300px viewports
  //                (kept as a div background — it covers the entire
  //                 hero box so the image's intrinsic dimensions
  //                 don't matter here).
  //   .img-side — wide-desktop column. Contains a tight-wrapping
  //                .img-frame around a real <img> element so the
  //                corner brackets (::before / ::after on .img-frame)
  //                anchor to the IMAGE'S actual bounding box, not the
  //                column's. Switching to <img> instead of a CSS
  //                background was deliberate — pseudo-elements on a
  //                background-image div would anchor to the div's
  //                box, which only coincides with the image edges
  //                when the image happens to fill it perfectly.
  // SCSS toggles which image element is visible via the min-width:
  // 1300px media query — no resize listener or re-render needed.
  heroContainer.innerHTML = `
    ${atmosphereImg ? `<div class="hero-atmosphere" aria-hidden="true" style="background-image:url('${atmosphereImg}')"></div>` : ''}
    ${heroImg ? `<div class="hero-img" aria-hidden="true" style="background-image:url('${heroImg}')"></div>` : ''}
    ${heroImg ? `
      <div class="img-side" aria-hidden="true">
        <div class="img-frame">
          <img src="${heroImg}" alt="" />
        </div>
      </div>
    ` : ''}
    <div class="wrap">
      <div class="text position-relative">
        ${docId ? getSanityEditLink(docId, 'caseStudy', 'title', 'Edit Hero') : ''}
        ${eyebrow ? `<div class="eyebrow">${eyebrow}</div>` : ''}
        <h1>${study.title || ''}</h1>
        ${dek ? `<p class="dek">${dek}</p>` : ''}
        <div class="rule"></div>
        ${showMeta ? `
          <div class="meta">
            ${tags.map((t) => `<span><span class="dot"></span> ${t}</span>`).join('')}
          </div>
        ` : ''}
      </div>
    </div>
  `;
}

function renderSections(study, docId) {
  if (!contentContainer) return;
  const sections = Array.isArray(study?.sections) ? study.sections : [];
  if (sections.length === 0) {
    contentContainer.innerHTML = '<p class="text-center py-5">Content not available.</p>';
    return;
  }

  contentContainer.innerHTML = sections.map((section, idx) => {
    const id = slugify(section.title || `section-${idx + 1}`);
    const conclusion = isConclusion(section);
    const editLink = docId
      ? (section._key
          ? getSanityEditLink(docId, 'caseStudy', `sections[_key=="${section._key}"]`, 'Edit Section')
          : getSanityEditLink(docId, 'caseStudy', `sections[${idx}]`, 'Edit Section'))
      : '';
    const inner = `
      ${editLink}
      ${section.title ? `<h2>${section.title}</h2>` : ''}
      ${section.content || ''}
    `;
    if (conclusion) {
      return `
        <section class="sec conclusion position-relative" id="${id}">
          <div class="inner">${inner}</div>
        </section>
      `;
    }
    return `
      <section class="sec position-relative" id="${id}">
        ${inner}
      </section>
    `;
  }).join('');

}

function renderTags(study) {
  if (!tagsContainer) return;
  const tags = getStudyTagList(study);
  if (study.showTags === false || tags.length === 0) {
    tagsContainer.hidden = true;
    return;
  }
  tagsContainer.hidden = false;
  tagsContainer.innerHTML = `
    <span class="label">This work touched</span>
    ${tags.map((t) => `
      <a class="tag" href="/case-studies/?tag=${encodeURIComponent(t)}">
        <span class="swatch" style="background:${swatchFor(t)}"></span>${t}
      </a>
    `).join('')}
  `;
}

// Pick the "Next case study" card: random draw from the candidate
// pool. If the author curated relatedCaseStudies, that's the pool;
// otherwise the pool is every other published case study. Random
// per page-load — refreshing the page shows a different case, so
// repeat visitors keep discovering work.
function pickNextCase(currentStudy, allStudies) {
  let pool;
  if (Array.isArray(currentStudy.relatedCaseStudies) && currentStudy.relatedCaseStudies.length > 0) {
    pool = currentStudy.relatedCaseStudies;
  } else if (Array.isArray(allStudies)) {
    pool = allStudies.filter((s) => s.title !== currentStudy.title);
  }
  if (!pool || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

function renderNextCard(study, allStudies) {
  if (!nextCardContainer) return;
  if (study.showRelated === false) {
    nextCardContainer.hidden = true;
    return;
  }
  const next = pickNextCase(study, allStudies);
  if (!next) {
    nextCardContainer.hidden = true;
    return;
  }
  // Prefer slug for the outbound link (matches the new canonical
  // URL shape). Falls back to title if a study is missing a slug,
  // which the legacy ?title= path in init() can still resolve.
  const href = next.slug
    ? `/case-studies/case-study?slug=${encodeURIComponent(next.slug)}`
    : next.title
      ? `/case-studies/case-study?title=${encodeURIComponent(next.title)}`
      : '#';
  nextCardContainer.href = href;
  nextCardContainer.hidden = false;
  nextCardContainer.innerHTML = `
    <div class="eyebrow"><span class="dot"></span> Next case study</div>
    <h3>${next.title || ''}</h3>
    ${next.description ? `<p>${next.description}</p>` : ''}
    <div class="arrow">Read →</div>
  `;
}

function renderTalkCta(study) {
  if (!talkCtaContainer) return;
  talkCtaContainer.hidden = study.showCta === false;
}

// ── TOC + scroll-spy ──────────────────────────────────────────
function buildToc() {
  if (!tocList || !contentContainer) return;
  tocList.innerHTML = '';
  const sections = [...contentContainer.querySelectorAll('.sec')];
  sections.forEach((s) => {
    const title = s.querySelector('h2');
    if (!title) return;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${s.id}`;
    a.textContent = title.textContent.trim();
    a.dataset.target = s.id;
    li.appendChild(a);
    tocList.appendChild(li);
  });
  const tocLinks = [...tocList.querySelectorAll('a')];
  if (tocLinks.length === 0) return;
  const setActive = (id) => {
    tocLinks.forEach((a) => a.classList.toggle('is-active', a.dataset.target === id));
  };
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
    },
    { rootMargin: '-30% 0px -55% 0px', threshold: 0 }
  );
  sections.forEach((s) => io.observe(s));
}

// ── TOC sticky anchor ────────────────────────────────────────
// The TOC is `position: absolute` (see case-study-detail.scss). This
// handler writes its `top` in document coordinates each scroll tick:
//   - While the hero is in view, top = article's top in the document,
//     so the TOC sits beside the first section.
//   - Once the user scrolls past, top = scrollY + STICK_OFFSET, which
//     pins the TOC 160 px below the viewport top (clearing the 100 px
//     site navbar with breathing room).
// Using absolute (not fixed) means the TOC scrolls with the page while
// the hero is on screen, then "sticks" — same effect as
// position: sticky but without needing a flex/grid layout around it.
function bindTocSticky() {
  const tocEl = document.querySelector('.cs-detail .toc');
  if (!tocEl) return;
  const STICK_OFFSET = 160;
  // Anchor to the first .sec's top so the TOC's "IN THIS CASE" header
  // sits on the same baseline as the first section's opening text,
  // not at the top of the article's padding band above it.
  const getAnchorTopDoc = () => {
    const firstSec = document.querySelector('.cs-detail .article .sec');
    const target = firstSec || document.querySelector('.cs-detail .article');
    if (!target) return 0;
    return target.getBoundingClientRect().top + window.scrollY;
  };
  const onScroll = () => {
    const anchorTopDoc = getAnchorTopDoc();
    const top = Math.max(anchorTopDoc, window.scrollY + STICK_OFFSET);
    tocEl.style.top = `${top}px`;
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
}

// ── Scroll progress bar ───────────────────────────────────────
function bindProgressBar() {
  if (!progressBar) return;
  const onScroll = () => {
    const doc = document.documentElement;
    const h = doc.scrollHeight - doc.clientHeight;
    const pct = h > 0 ? Math.min(100, Math.max(0, (window.scrollY / h) * 100)) : 0;
    progressBar.style.width = pct + '%';
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ── Site navbar: transparent → solid + auto-hide on scroll-down ─
// Medium / Substack / NYT Reader pattern. The navbar is fully
// transparent over the hero, fades to translucent paper-cream after
// 80px of scroll, hides on accumulated downward scroll, and reappears
// the moment the user scrolls up. Hide/show is gated by direction
// thresholds to debounce trackpad jitter. Always visible at top.
function bindNavBehavior() {
  const nav = document.querySelector('.navbar.fixed-top');
  if (!nav) return;
  let lastY = window.scrollY;
  let lastDir = 0;
  const HIDE_THRESHOLD = 12;
  const SHOW_THRESHOLD = 6;
  const SOLID_AFTER = 80;
  const TOP_LOCK = 80;

  const onScroll = () => {
    const y = window.scrollY;
    const dy = y - lastY;
    lastY = y;

    nav.classList.toggle('is-solid', y > SOLID_AFTER);

    if (y < TOP_LOCK) {
      nav.classList.remove('is-hidden');
      lastDir = 0;
      return;
    }
    if (Math.sign(dy) === Math.sign(lastDir)) lastDir += dy;
    else lastDir = dy;
    if (lastDir > HIDE_THRESHOLD) {
      nav.classList.add('is-hidden');
      lastDir = 0;
    } else if (lastDir < -SHOW_THRESHOLD) {
      nav.classList.remove('is-hidden');
      lastDir = 0;
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

// ── Smooth-scroll for in-page anchors (TOC etc) ───────────────
function bindSmoothScroll() {
  if (!root) return;
  root.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const id = a.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      // Offset = site navbar height (100px) + small breathing space.
      const y = target.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: 'smooth' });
      history.replaceState(null, '', '#' + id);
    });
  });
}

// ── Entry ─────────────────────────────────────────────────────
async function init() {
  if (!root) return;

  // Resolve which case study to render by checking, in order:
  //   1. ?slug=…   — new canonical URL key
  //   2. ?title=…  — legacy shared links (kept for safety; the
  //                 production htaccess 301s these to ?slug=)
  //   3. data-title — the per-study legacy HTML templates in
  //                 src/html/case-studies/<name>.html bake the title
  //                 into the markup so they render without any query.
  const urlParams = new URLSearchParams(window.location.search);
  const slug = urlParams.get('slug');
  let title = urlParams.get('title');
  if (!slug && (!title || title.trim() === '')) {
    title = contentContainer ? contentContainer.getAttribute('data-title') : null;
  }
  if (!slug && !title) {
    console.warn('No case study slug/title provided via URL or data-title');
    return;
  }

  const result = await fetchCaseStudy({ slug, title });
  if (!result || !result.currentStudy) {
    console.error('Case study not found in Sanity');
    if (contentContainer) {
      contentContainer.innerHTML = '<p class="text-center py-5">Case study not found.</p>';
    }
    return;
  }
  const { currentStudy, allCaseStudies, docId } = result;

  // Normalize the address bar to the slug-based canonical URL when
  // a legacy ?title= / data-title / non-slug path resolved a real
  // study with a slug. This is mostly belt-and-braces — the
  // production htaccess already 301s known legacy URLs before the
  // page loads; this catches dev contexts and edge cases.
  if (currentStudy.slug && !slug) {
    const canonical = `${window.location.pathname.replace(/\.html$/, '')}?slug=${encodeURIComponent(currentStudy.slug)}${window.location.hash}`;
    window.history.replaceState(null, '', canonical);
  }

  document.title = `${currentStudy.title} — Coriolis Technologies`;

  // Visibility toggles
  if (currentStudy.showHero === false) {
    if (heroContainer) heroContainer.style.display = 'none';
  } else {
    renderHero(currentStudy, docId);
  }

  if (currentStudy.showSections === false) {
    if (contentContainer) contentContainer.style.display = 'none';
  } else {
    renderSections(currentStudy, docId);
    buildToc();
  }

  renderTags(currentStudy);
  renderNextCard(currentStudy, allCaseStudies);
  renderTalkCta(currentStudy);

  bindProgressBar();
  bindNavBehavior();
  bindSmoothScroll();
  bindTocSticky();
  initScrollAnimations();
}

document.addEventListener('DOMContentLoaded', init);
